import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { getAgencyDetail} from "store/agency/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_BANNER_VISIBLE_MENU,
  LIST_OPTION_BANNER_VISIBLE_MENU,
} from "helpers/constans";
import { formatDateStringToKorea } from "helpers/format";
import { unique } from "helpers/validate_yup";
import "react-phone-number-input/style.css";
import { NumericFormat } from "react-number-format";
import { postBannerPosition, putBannerPosition } from "store/banner-position/thunk";

export interface Props {
  id?: string;
  item?: any;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);

const PARAMS_KEY_DEFINE = ["agency", "campaign"];

const BannerPositionForm = ({
  id = "",
  item = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {...values};
      const response: any = id
        ? await putBannerPosition(id, data)
        : await postBannerPosition(data);

      if (response?.data || response?.code == 200) {
        setIsLoading((_prev) => false);
        toast(
          `${
            id
              ? t("The banner position has been updated.")
              : t("The banner position has been created.")
          }`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    positionCode: Yup.string().required(`${t("This field is required")}`),
    name: Yup.string().required(`${t("This field is required")}`),
    visibleInMenu: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      positionCode: "",
      name: "",
      visibleInMenu: "",
      setting: {
        autoSlide: true,
        slideDurationMillis: undefined,
        slideIntervalMillis: undefined,
      },
      layoutConfigs: {
        phone: {
          height: undefined
        },
        tablet: {
          height: undefined
        },
        foldSub: {
          height: undefined
        },
        foldMain: {
          height: undefined
        }
      },
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    if(item) {
      formik.setValues({
        ...item,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    document.title = `${t("Create Banner Position")} - ${t(
      "Banner Position"
    )} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Banner Position") : t("Create Banner Position")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 3) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  {
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="positionCode" className="form-label">
                          {t("Position Code")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="positionCode"
                          type="text"
                          className="form-control"
                          id="positionCode"
                          disabled={!!id}
                          placeholder={t("Enter your position code")}
                          value={formik?.values?.positionCode}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "positionCode",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.positionCode &&
                        formik.errors.positionCode ? (
                          <div className="text-danger mt-2">
                            {formik.errors.positionCode}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  }

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder={t("Name")}
                        value={formik?.values?.name}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Visible In Menu")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownStatusNew
                        name="visibleInMenu"
                        placeholder={`${t("Visible In Menu")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.visibleInMenu || null}
                        dataList={LIST_OPTION_BANNER_VISIBLE_MENU}
                        onChangeSelect={(visibleInMenu: any) => {
                          formik.setFieldValue(
                            "visibleInMenu",
                            visibleInMenu?.value
                          );
                        }}
                        isHasOptionAll={false}
                        colors={COLOR_BANNER_VISIBLE_MENU}
                      />

                      {formik?.touched?.visibleInMenu &&
                      formik?.errors?.visibleInMenu ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.visibleInMenu}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div>
                      <Row>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="slideIntervalMillis"
                              className="form-label"
                            >
                              {t("Slide Interval Millis")}{" "}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.setting?.slideIntervalMillis} 
                              placeholder={t("Slide Interval Millis")}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("setting.slideIntervalMillis", floatValue)
                              }}
                            />
                            {formik.touched.setting?.slideIntervalMillis &&
                            formik.errors.setting?.slideIntervalMillis ? (
                              <div className="text-danger mt-2">
                                {formik.errors.setting?.slideIntervalMillis}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="slideDurationMillis"
                              className="form-label"
                            >
                              {t("Slide Duration Millis")}{" "}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.setting?.slideDurationMillis} 
                              placeholder={t("Slide Duration Millis")}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("setting.slideDurationMillis", floatValue)
                              }}
                            />
                            
                            {formik.touched.setting?.slideDurationMillis &&
                            formik.errors.setting?.slideDurationMillis ? (
                              <div className="text-danger mt-2">
                                {formik.errors.setting?.slideDurationMillis}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="mb-3">
                            <Label className="form-label">
                              {t("Auto Slide")}
                            </Label>
                            <div>
                              <div
                                className="form-check form-switch form-switch-sm text-center"
                                dir="ltr"
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  name="setting-auto-slide"
                                  checked={formik?.values?.setting?.autoSlide}
                                  onChange={(event) => {
                                    formik.setFieldValue(
                                      "setting.autoSlide",
                                      event?.target?.checked
                                    );
                                  }}
                                />
                              </div>
                            </div>

                            {formik?.touched?.setting?.autoSlide &&
                            formik?.errors?.setting?.autoSlide ? (
                              <div className="text-danger mt-2">
                                {formik?.errors?.setting?.autoSlide}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label>{t("Layout Configs")}</Label>
                      <Row>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="slideIntervalMillis"
                              className="form-label"
                            >
                              {`${t("Fold Main")} - ${t("Height")}`}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.layoutConfigs?.foldMain?.height} 
                              placeholder={`${t("Fold Main")} - ${t("Height")}`}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("layoutConfigs.foldMain.height", floatValue)
                              }}
                            />
                            {formik.touched.layoutConfigs?.foldMain?.height &&
                            formik.errors.layoutConfigs?.foldMain?.height ? (
                              <div className="text-danger mt-2">
                                {formik.errors.layoutConfigs?.foldMain?.height}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="slideIntervalMillis"
                              className="form-label"
                            >
                              {`${t("Fold Sub")} - ${t("Height")}`}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.layoutConfigs?.foldSub?.height} 
                              placeholder={`${t("Fold Sub")} - ${t("Height")}`}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("layoutConfigs.foldSub.height", floatValue)
                              }}
                            />
                            {formik.touched.layoutConfigs?.foldSub?.height &&
                            formik.errors.layoutConfigs?.foldSub?.height ? (
                              <div className="text-danger mt-2">
                                {formik.errors.layoutConfigs?.foldSub?.height}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="tablet"
                              className="form-label"
                            >
                              {`${t("Tablet")} - ${t("Height")}`}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.layoutConfigs?.tablet?.height} 
                              placeholder={`${t("Tablet")} - ${t("Height")}`}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("layoutConfigs.tablet.height", floatValue)
                              }}
                            />
                            {formik.touched.layoutConfigs?.tablet?.height &&
                            formik.errors.layoutConfigs?.tablet?.height ? (
                              <div className="text-danger mt-2">
                                {formik.errors.layoutConfigs?.tablet?.height}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label
                              htmlFor="phone"
                              className="form-label"
                            >
                              {`${t("Phone")} - ${t("Height")}`}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.layoutConfigs?.phone?.height} 
                              placeholder={`${t("Phone")} - ${t("Height")}`}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("layoutConfigs.phone.height", floatValue)
                              }}
                            />
                            {formik.touched.layoutConfigs?.phone?.height &&
                            formik.errors.layoutConfigs?.phone?.height ? (
                              <div className="text-danger mt-2">
                                {formik.errors.layoutConfigs?.phone?.height}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Update Banner Position")
                          : t("Create Banner Position")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default BannerPositionForm;
